import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AddLink as AddLinkIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  DeleteOutline as DeleteOutlineIcon,
  PlaylistAdd as PlaylistAddIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import AnalyticsService from '../../services/AnalyticsService';
import { ChannelAvatar, Section } from '../../components/analytics/AnalyticsParts';
import { ChannelMenu, UserTagChips } from '../../components/analytics/ChannelRows';
import { ImportDialog } from '../../components/analytics/ChannelDialogs';
import ConnectionDialog from '../../components/analytics/ConnectionDialog';
import { formatDateTime, formatShare } from '../../utils/analyticsFormat';

function apiError(error, fallback) {
  return error.response?.data?.error || fallback;
}

function FolderRow({ folder, index, count, onRename, onMove, onDelete }) {
  const [name, setName] = useState(folder.name);
  useEffect(() => setName(folder.name), [folder.name]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderTop: index ? 1 : 0, borderColor: 'divider' }}>
      <TextField
        size="small"
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={() => name.trim() && name.trim() !== folder.name && onRename(folder, name.trim())}
        onKeyDown={(event) => event.key === 'Enter' && event.target.blur()}
        inputProps={{ 'aria-label': `Folder name for ${folder.name}`, maxLength: 80 }}
        sx={{ flex: 1, minWidth: 0 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ width: 90, textAlign: 'right' }}>
        {folder.channelCount} {folder.channelCount === 1 ? 'channel' : 'channels'}
      </Typography>
      <IconButton aria-label={`Move ${folder.name} up`} disabled={index === 0} onClick={() => onMove(index, -1)}>
        <ArrowUpwardIcon fontSize="small" />
      </IconButton>
      <IconButton aria-label={`Move ${folder.name} down`} disabled={index === count - 1} onClick={() => onMove(index, 1)}>
        <ArrowDownwardIcon fontSize="small" />
      </IconButton>
      <IconButton aria-label={`Delete ${folder.name}`} onClick={() => onDelete(folder)}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function FoldersSection({ folders, reload }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const run = async (action, fallback) => {
    setError('');
    try {
      await action();
      await reload();
    } catch (err) {
      setError(apiError(err, fallback));
    }
  };

  const create = () =>
    name.trim() &&
    run(async () => {
      await AnalyticsService.createFolder(name.trim());
      setName('');
    }, 'Could not create the folder.');

  const move = (index, direction) => {
    const ids = folders.map((folder) => folder.id);
    [ids[index], ids[index + direction]] = [ids[index + direction], ids[index]];
    run(() => AnalyticsService.reorderFolders(ids), 'Could not reorder folders.');
  };

  return (
    <Section
      title="Your folders"
      subtitle="Group channels to compare them and to filter totals."
    >
      <Box sx={{ display: 'flex', gap: 1, mb: 2, maxWidth: 520 }}>
        <TextField
          size="small"
          label="New folder"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && create()}
          inputProps={{ maxLength: 80 }}
          sx={{ flex: 1 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={create} disabled={!name.trim()}>
          Add folder
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {folders.length ? (
        <Box sx={{ maxWidth: 720 }}>
          {folders.map((folder, index) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              index={index}
              count={folders.length}
              onRename={(target, next) =>
                run(() => AnalyticsService.renameFolder(target.id, next), 'Could not rename the folder.')
              }
              onMove={move}
              onDelete={(target) =>
                run(() => AnalyticsService.deleteFolder(target.id), 'Could not delete the folder.')
              }
            />
          ))}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Deleting a folder moves its channels to Ungrouped. Use a channel's ⋮ menu on the
            Overview to move it into a folder.
          </Typography>
        </Box>
      ) : (
        <Typography color="text.secondary">No folders yet. Channels without a folder are shown as Ungrouped.</Typography>
      )}
    </Section>
  );
}

const UPLOAD_CHANNEL_LABELS = { sleep: 'Sleep', 'ai-stories': 'AI Stories' };

function connectionLabel(channel) {
  if (channel.source === 'import') return 'YouTube Studio import';
  if (channel.uploadConnectionKind) {
    const uploadChannel =
      channel.uploadChannelName || `its ${UPLOAD_CHANNEL_LABELS[channel.uploadConnectionKind]} channel`;
    return `Through the upload connection of ${uploadChannel}`;
  }
  return 'Connected through proxy';
}

function linkResultMessage({ added }) {
  if (!added) return 'Every upload channel connected to YouTube is already here.';
  return `Added ${added} ${added === 1 ? 'channel' : 'channels'}. Their history is being pulled now; this can take a few minutes.`;
}

function lastUpdated(channel) {
  if (channel.source === 'import')
    return channel.lastImportedAt ? `Imported ${formatDateTime(channel.lastImportedAt)}` : 'No export uploaded yet';
  if (!channel.connected) return 'Waiting for Google sign-in';
  if (channel.status === 'paused') return 'Scheduled pulls paused';
  if (!channel.lastSyncedAt) return 'Pulling its history now';
  return `Pulled ${formatDateTime(channel.lastSyncedAt)}`;
}

// The scheduled pull hours in the viewer's time, earliest first, such as
// "5:00 AM, 11:00 AM, 5:00 PM, and 11:00 PM".
function pullTimes(pullHoursUtc) {
  const times = pullHoursUtc
    .map((hour) => new Date(Date.UTC(2000, 0, 1, hour)))
    .sort((a, b) => a.getHours() - b.getHours())
    .map((time) => time.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }));
  return new Intl.ListFormat(undefined, { type: 'conjunction' }).format(times);
}

function ChannelsSection({ channels, folders, existingTags, reload, pullHoursUtc, showRevenue }) {
  const [dialog, setDialog] = useState(null);
  const [linking, setLinking] = useState(false);
  const [linkResult, setLinkResult] = useState(null);

  async function linkUploadChannels() {
    setLinking(true);
    setLinkResult(null);
    try {
      const result = await AnalyticsService.linkUploadChannels();
      setLinkResult({ severity: 'success', text: linkResultMessage(result) });
      if (result.added) reload();
    } catch (err) {
      setLinkResult({ severity: 'error', text: apiError(err, 'Could not add your upload channels.') });
    } finally {
      setLinking(false);
    }
  }

  const pullNote = `Connected channels are pulled from YouTube at ${pullTimes(pullHoursUtc)} your time, including their full history the first time.`;

  return (
    <Section
      title="Your channels"
      subtitle={
        showRevenue
          ? `${pullNote} Channels you reach through a CMS can be added from YouTube Studio exports.`
          : `${pullNote} Sleep and AI Stories channels connected to YouTube can be added with their existing connection.`
      }
    >
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<PlaylistAddIcon />}
          onClick={linkUploadChannels}
          disabled={linking}
        >
          Add upload channels
        </Button>
        <Button startIcon={<AddLinkIcon />} onClick={() => setDialog('connect')} sx={{ bgcolor: 'background.paper' }}>
          Connect YouTube channel
        </Button>
        {showRevenue && (
          <Button startIcon={<UploadFileIcon />} onClick={() => setDialog('import')} sx={{ bgcolor: 'background.paper' }}>
            Import from YouTube Studio
          </Button>
        )}
      </Box>
      {linkResult && (
        <Alert severity={linkResult.severity} onClose={() => setLinkResult(null)} sx={{ mb: 2 }}>
          {linkResult.text}
        </Alert>
      )}
      {channels.length ? (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Channel</TableCell>
                <TableCell>Data</TableCell>
                {showRevenue && <TableCell align="right">Your share</TableCell>}
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.map((channel) => (
                <TableRow key={channel.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <ChannelAvatar channel={channel} size={32} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {channel.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {connectionLabel(channel)}
                        </Typography>
                        {channel.tags.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            <UserTagChips tags={channel.tags} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{lastUpdated(channel)}</Typography>
                    {channel.problem && (
                      <Typography variant="caption" color="error.main" component="div">
                        {channel.problem}
                      </Typography>
                    )}
                  </TableCell>
                  {showRevenue && (
                    <TableCell align="right">{formatShare(channel.revenueShare)}</TableCell>
                  )}
                  <TableCell align="right">
                    <ChannelMenu
                      channel={channel}
                      folders={folders}
                      existingTags={existingTags}
                      onChanged={reload}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary">
          {showRevenue
            ? 'No channels yet. Add your upload channels, connect one, or import a YouTube Studio export.'
            : 'No channels yet. Add your upload channels or connect one.'}
        </Typography>
      )}
      {dialog === 'connect' && (
        <ConnectionDialog
          onClose={() => {
            setDialog(null);
            reload();
          }}
          onChanged={reload}
        />
      )}
      {dialog === 'import' && <ImportDialog onClose={() => setDialog(null)} onImported={reload} />}
    </Section>
  );
}

export default function ManageTab({ summary, reload, showRevenue }) {
  const [pullHoursUtc, setPullHoursUtc] = useState([0, 6, 12, 18]);

  useEffect(() => {
    AnalyticsService.syncStatus()
      .then((status) => setPullHoursUtc(status.pullHoursUtc))
      .catch(() => {});
  }, []);

  const channels = summary.groups
    .flatMap((group) => group.channels)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <ChannelsSection
        channels={channels}
        folders={summary.folders}
        existingTags={summary.tags}
        reload={reload}
        pullHoursUtc={pullHoursUtc}
        showRevenue={showRevenue}
      />
      <FoldersSection folders={summary.folders} reload={reload} />
    </>
  );
}
